import { forwardRef } from 'react';
import { components as RsComponents, defaultTheme } from 'react-select';
import styled, { createGlobalStyle } from 'styled-components';
import {
  BaseReactSelect,
  SelectProps,
  StyledTag,
  RocketlaneUIControl,
  AsyncSelect as BaseAsyncSelect,
} from 'components/atoms/select';
import { CurrencyOption } from 'components/atoms/select/custom-components';
import { useAntdRlUISwitcher } from 'contexts/antd-rlui-switcher';

/* New design system styles */
export const NEW_DESIGN_STYLES = {
  MENU: {
    borderRadius: 'var(--spacing-03)',
    boxShadow:
      '0 0.6px 1.8px rgba(0, 0, 0, 0.11), 0 3.2px 7.2px rgba(0, 0, 0, 0.13)',
    padding: 'var(--padding-xs)',
  },
  OPTION: {
    width: '96%',
    margin: '0 auto',
    borderRadius: 'var(--spacing-02)',
  },
  CONTROL: {
    borderRadius: 'var(--spacing-02)',
    minHeight: 'var(--height-base)',
  },
  MULTI_VALUE: {
    minHeight: 'var(--height-sm)',
    height: 'var(--height-sm)',
    margin: '0',
    alignItems: 'center',
    padding: 0,
  },
  MULTI_VALUE_LABEL: {
    padding: 0,
    paddingLeft: 0,
    paddingRight: 0,
    fontSize: 'var(--font-size-sm)',
    lineHeight: 'var(--spacing-05)',
  },
  MULTI_VALUE_REMOVE: {
    padding: '0 var(--padding-xs)',
    alignItems: 'center',
  },
  COLORS: {
    selected: 'var(--blue-20)',
    focused: 'var(--hover-color)',
  },
} as const;

export const createNewDesignStyles = (
  designStyles: typeof NEW_DESIGN_STYLES,
  userStyles: Record<string, any> = {},
) => ({
  ...userStyles,
  menuPortal: (base: any, state: any) => ({
    zIndex: 'var(--zindex-popover-low)',
    ...(userStyles.menuPortal ? userStyles.menuPortal(base, state) : base),
  }),
  menu: (base: any, state: any) => {
    return {
      ...(userStyles.menu
        ? userStyles.menu(base, state)
        : { ...base, ...designStyles.MENU }),
    };
  },
  control: (base: any, state: any) => ({
    ...(userStyles.control
      ? userStyles.control(base, state)
      : {
          ...designStyles.CONTROL,
          ...base,
        }),
  }),
  valueContainer: (base: any, state: any) => {
    const userResult = userStyles.valueContainer
      ? userStyles.valueContainer(base, state)
      : base;

    return {
      ...userResult,
      height: '100%',
      padding: '0 var(--padding-01)',
      display: 'flex',
      alignItems: state.isMulti ? 'flex-start' : 'center',
      flexWrap: state.isMulti ? 'wrap' : 'nowrap',
      gap: state.isMulti ? 'var(--padding-xs)' : '0',
    };
  },
  multiValue: (base: any, state: any) =>
    userStyles.multiValue
      ? userStyles.multiValue(base, state)
      : {
          ...base,
          ...designStyles.MULTI_VALUE,
        },
  multiValueLabel: (base: any, state: any) =>
    userStyles.multiValueLabel
      ? userStyles.multiValueLabel(base, state)
      : {
          ...base,
          ...designStyles.MULTI_VALUE_LABEL,
        },
  multiValueRemove: (base: any, state: any) =>
    userStyles.multiValueRemove
      ? userStyles.multiValueRemove(base, state)
      : {
          ...base,
          ...designStyles.MULTI_VALUE_REMOVE,
        },
  singleValue: (base: any, state: any) => ({
    ...(userStyles.singleValue ? userStyles.singleValue(base, state) : base),
    margin: 0,
    padding: 0,
    lineHeight: 1.5,
  }),
  placeholder: (base: any, state: any) => ({
    ...(userStyles.placeholder ? userStyles.placeholder(base, state) : base),
    margin: 0,
    padding: 0,
    lineHeight: 1.5,
  }),
  input: (base: any, state: any) => ({
    ...(userStyles.input ? userStyles.input(base, state) : base),
    margin: 0,
    padding: 0,
  }),
  option: (base: any, state: any) => {
    const getBackgroundColor = () => {
      if (state.isSelected) {
        return designStyles.COLORS.selected;
      }
      if (state.isFocused) {
        return designStyles.COLORS.focused;
      }

      return base.backgroundColor;
    };

    // const hasBackground = state.isSelected || state.isFocused;
    const hasDescription = !!state.data?.description;
    const optionHeight = hasDescription ? '56px' : '32px';

    return {
      ...(userStyles.option
        ? userStyles.option(base, state)
        : {
            ...base,
          }),
      backgroundColor: getBackgroundColor(),
      color: 'var(--text-primary)',
      borderRadius: 'var(--border-radius-small)',
      marginTop: '0px',
      marginBottom: '0px',
      minHeight: optionHeight,
    };
  },
});

const GlobalSelectStyles = createGlobalStyle`
  /* Apply OPTION styles when menu is rendered in portal (outside component tree) */
  /* Only applies when RocketUISelect is mounted (new design enabled) */
  .ozone-select__menu-portal {
    .ozone-select__option {
      width: 96%;
      margin: 10px auto 0px;
      border-radius: var(--spacing-02);

      [data-new-theme-enabled='true'] & {
        border-radius: var(--border-radius-small);
        padding-left: var(--padding-xs);
        padding-right: var(--padding-xs);
        margin-top: 0px;
        margin-bottom: 0px;
        min-height: var(--height-base);
        margin: 0 auto;
      }
    }
  }

  [data-new-theme-enabled='true'] & {
  .ozone-select__menu {
    .ozone-select__option {
      border-radius: var(--border-radius-small);
      padding: var(--padding-xs) var(--padding-01) !important;
      margin-left: 0px;
      margin-right: 0px;
      min-height: var(--height-base);
      width: 100%;
      align-content: center;
    }
  }

  .ozone-select__value-container{
    height: 100% !important;
    padding: var(--padding-xs) var(--padding-01) !important;
    display: flex !important;
    /* align-content: center !important; */
  }
  .ozone-select__menu-list{
    padding: 0px;
  }

  .ozone-select__placeholder {
    position: absolute;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.5 !important;
  }

  .ozone-select__input-container {
    margin: 0 !important;
    padding: 0 !important;
  }

  .ozone-select__group {
    padding-left: 4px;
    padding-right: 4px;
  }
}
`;

export const NewStyledRSOption = styled(RsComponents.Option).attrs({
  theme: {
    ...defaultTheme,
    borderRadius: 0,
  },
})`
  width: 96%;
  margin: 0 auto;
  [data-new-theme-enabled='true'] & {
    border-radius: var(--border-radius-small);
    padding: var(--padding-03) var(--padding-01) !important;
    margin-top: 0px;
    margin-bottom: 0px;
    min-height: var(--height-base);
  }
`;

export const NewTagOption = (props) => {
  const { data } = props;

  return (
    <NewStyledRSOption {...props}>
      <StyledTag
        title={data.label}
        color={data.color}
        data-cy={`tag-option-${data.label}`}
      >
        {data.label}
      </StyledTag>
    </NewStyledRSOption>
  );
};

const RocketUISelect = forwardRef<any, SelectProps>(
  ({ styles, ...props }, ref) => {
    const computedStyles = createNewDesignStyles(
      NEW_DESIGN_STYLES,
      styles || {},
    );
    const isNew = useAntdRlUISwitcher();

    return (
      <>
        <GlobalSelectStyles />
        <BaseReactSelect
          {...props}
          components={{
            Control: RocketlaneUIControl,
            ...props.components,
          }}
          styles={computedStyles}
          ref={ref}
          size={isNew ? 'small' : undefined}
        />
      </>
    );
  },
);

const RocketUIAsyncSelect = forwardRef<any, SelectProps>(
  ({ styles, ...props }, ref) => {
    const computedStyles = createNewDesignStyles(
      NEW_DESIGN_STYLES,
      styles || {},
    );
    const isNew = useAntdRlUISwitcher();

    return (
      <BaseAsyncSelect
        {...props}
        components={{
          Control: RocketlaneUIControl,
          ...props.components,
        }}
        styles={computedStyles}
        ref={ref}
        size={isNew ? 'small' : undefined}
      />
    );
  },
);

export const Select = forwardRef<any, SelectProps>((props, ref) => {
  const isNewDesignEnabled = useAntdRlUISwitcher();

  if (isNewDesignEnabled) {
    return <RocketUISelect {...props} ref={ref} />;
  }

  return <BaseReactSelect {...props} ref={ref} />;
});

export const AsyncSelect = forwardRef<any, SelectProps>((props, ref) => {
  const isNewDesignEnabled = useAntdRlUISwitcher();

  if (isNewDesignEnabled) {
    return <RocketUIAsyncSelect {...props} ref={ref} />;
  }

  return <BaseAsyncSelect {...props} ref={ref} />;
});

export const CurrencySelect = forwardRef<any, SelectProps>((props, ref) => (
  <Select
    {...props}
    components={{ Option: CurrencyOption, ...props.components }}
    ref={ref}
  />
));

Select.displayName = 'Select';
export {
  CreatableSelect,
  AsyncCreatableSelect,
} from 'components/facade/creatable-select';
